// @ts-nocheck

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import { ON_MY_MATCH_TOGGLE_TRANSITION_DURATION } from '../../constants/ui/mymatch';
import { useCSSVariablesContext } from '../../contexts/CSSVariablesContextProvider';
import { useViewportContext } from '../../contexts/ViewportContext';
import { getAppDOMElement } from '../../DOMElements';
import useMatchViewContext from '../../hooks/useMatchViewContext';
import MatchCardAnimationContainer from '../../modules/animations/containers/MatchCardAnimationContainer';
import { MatchEntityStatus } from '../../states/entities/matches';
import theme from '../../styled-components/dark';
import { withOpacity } from '../../styled-components/dark/helpers';
import { LightlyWhiteHeaderBackgroundClassName } from '../../styled-components/shared';
import type { MatchStatus } from '../../types/match';
import InfosLabel, { InfosLabelTypes } from '../InfosLabel/InfosLabel';
import { MatchCardHeaderTimerClassName } from '../MatchCardHeader';
import MatchCardHeaderContainer from '../MatchCardHeader/MatchCardHeaderContainer';
import FullMatchTitle from '../MatchFullTitle';
import MatchSubTitle, { MatchSubTitleLabelClassName } from '../MatchSubTitle';
import MyMatchDynamicBackground, {
  MyMatchDynamicBackgroundLeftImgClassName,
  MyMatchDynamicBackgroundRadialClassName,
  MyMatchDynamicBackgroundRightImgClassName,
} from '../MyMatchDynamicBackground';
import ScoreboardContainer from '../Scoreboard/ScoreboardContainer';
import ScoreboardMultimatchContainer from '../ScoreboardMultimatch/ScoreboardMultimatchContainer';

const FIXED_OFFSET = 35; // px;

const Wrapper = styled.div`
  position: relative;
  margin: 0.6em;
  border-radius: ${theme.Card.borderRadius};
  overflow: hidden;
  ${({ $CSSVariablesStyleSheet }) => $CSSVariablesStyleSheet}
`;

const ContentWrapper = styled.div`
  overflow: hidden;
  transform-style: preserve-3d;
  border-radius: inherit;

  transition: background-color ${ON_MY_MATCH_TOGGLE_TRANSITION_DURATION}ms ease-in-out;
`;

const FixedPadding = styled.div`
  height: 0.3em;
  margin-top: -1px;
`;

const HeaderWrapper = styled.div`
  font-size: 0.8em;

  .full-match-title {
    font-size: 1.25em;
  }

  & .${MatchSubTitleLabelClassName} {
    transition: none ${ON_MY_MATCH_TOGGLE_TRANSITION_DURATION}ms ease-in-out;
    transition-property: opacity, color;
  }

  ${({ $myMatchViewModeIsAvailable }) => css`
    ${$myMatchViewModeIsAvailable
      ? css`
          & .${MatchSubTitleLabelClassName} {
            color: white;
            opacity: 0.5;
          }
        `
      : undefined}
  `}
`;

export const MatchHeaderClassName = 'match-header';
const Header = styled(MatchCardHeaderContainer)`
  position: relative;

  transition: background-color ${ON_MY_MATCH_TOGGLE_TRANSITION_DURATION}ms ease-in-out;
  --MatchCardHeader-mainBackgroundColor: transparent;

  & .badge {
    transition: background-color ${ON_MY_MATCH_TOGGLE_TRANSITION_DURATION}ms ease-in-out;
  }

  .${MatchCardHeaderTimerClassName} {
    transition: none ${ON_MY_MATCH_TOGGLE_TRANSITION_DURATION}ms ease-in-out;
    transition-property: opacity, color;
  }

  ${({ $myMatchViewModeIsAvailable }) => css`
    & .${LightlyWhiteHeaderBackgroundClassName} {
      opacity: 0.05;
    }

    & .badge {
      ${$myMatchViewModeIsAvailable
        ? css`
            background-color: #2a151a;
          `
        : undefined}
    }
  `}
`;

export const FullMatchTitleClassName = 'full-match-title';
const StyledFullMatchTitle = styled(FullMatchTitle)`
  background: transparent;
  position: relative;
`;

const StyledSubTitle = styled(MatchSubTitle)`
  margin: 0;
  padding: 0.4em;
`;

const SubHeaderWrapper = styled.div`
  ${({ $myMatchViewModeIsAvailable }) =>
    $myMatchViewModeIsAvailable
      ? css`
          transition: background-color ${ON_MY_MATCH_TOGGLE_TRANSITION_DURATION * 0.6}ms ease-in-out;
          background-color: transparent;
        `
      : css`
          transition: background-color ${ON_MY_MATCH_TOGGLE_TRANSITION_DURATION * 1.4}ms ease-in-out;
          background-color: ${theme.Match.SubHeader.backgroundColor};
        `}
`;

export const MatchScoreboardWidgetClassName = 'match-scoreboard-widget';
const ScoreboardWidget = styled(ScoreboardContainer)`
  position: relative;

  ${({ $isMultimatch }) =>
    $isMultimatch
      ? css`
          padding-top: 0;
        `
      : undefined}
`;

const FixedWrapper = styled.div`
  position: fixed;
  z-index: ${theme.ZIndices.middleColumn};
  top: 0;
  border-radius: ${theme.Card.borderRadius};
  overflow: hidden;

  transition: none 200ms ease-in-out;
  transition-property: visibility, transform;

  ${({ $offsetTop, $isFixed, $width, $height }) => css`
    visibility: ${$isFixed ? 'visible' : 'hidden'};
    transform: translateY(${$isFixed ? 0 : -$height}px);

    ${Target.__MOBILE__
      ? css`
          top: ${$offsetTop}px;
          width: 100%;
        `
      : css`
          top: 0;
          width: calc(${$width}px + 1.2em);
        `}
  `}

  ${Header}, ${StyledFullMatchTitle}, ${ScoreboardWidget} {
    margin-top: -1px;
  }

  ${({ $CSSVariablesStyleSheet }) => $CSSVariablesStyleSheet}
`;

const WarningsWrapper = styled.div`
  padding: 0.6em;
`;

const StyledInfosLabel = styled(InfosLabel)`
  margin: 0;
`;

const StyledScoreboardMultimatchContainer = styled(ScoreboardMultimatchContainer)`
  position: relative;
  font-size: ${({ $compact }) => ($compact ? '0.97em' : '1.2em')};
`;

const StyledMyMatchDynamicBackground = styled(MyMatchDynamicBackground)`
  --MyMatchDynamicBackground-defaultBackgroundColor: var(
    --MatchHeader-dynamicDefaultBackgroundColor,
    ${theme.MatchBetGroup.backgroundColor}
  );
  --MyMatchDynamicBackgroundRadial-backgroundImage: radial-gradient(
    ${withOpacity(theme.Match.Header.mmRadialColor, 'c7')},
    ${withOpacity(theme.Match.Header.mmRadialColor, '11')} 60%,
    transparent 70%,
    transparent
  );

  & .${MyMatchDynamicBackgroundLeftImgClassName} {
    height: 120%;
    top: -2em;
  }

  & .${MyMatchDynamicBackgroundRightImgClassName} {
    top: 10%;
    right: -15%;
  }

  & .${MyMatchDynamicBackgroundRadialClassName} {
    top: -1em;
  }
`;

type PropsType = {
  className?: string;
  matchId: number;
  sportId: number;
  matchStart: number;
  tvChannels: string;
  subTitle: string;
  competitor1Name: string;
  competitor1Flag?: string;
  competitor2Name: string;
  competitor2Flag?: string;
  competitor1NbRedCards?: number;
  competitor2NbRedCards?: number;
  periodName?: string;
  flagId?: string;
  categoryId: number;
  isOutright: boolean;
  title?: string;
  topName?: string;
  categoryName?: string;
  tournamentName?: string;
  roundName?: string;
  status?: MatchStatus;
  isMultimatch?: boolean;
  sportWarning?: string;
  categoryWarning?: string;
  tournamentWarning?: string;
  matchWarning?: string;
  onElementChange?: (...params: unknown) => unknown;
  fromBetBuilder?: boolean;
  simplified?: boolean;
  allowStickiness?: boolean;
  disableScroll?: boolean;
  subtitleRedirection?: string;
  myMatchViewModeIsAvailable: boolean;
};

const MatchHeader = ({
  matchId,
  sportId,
  matchStart,
  competitor1Name,
  competitor1Flag,
  competitor2Name,
  competitor2Flag,
  competitor1NbRedCards = 0,
  competitor2NbRedCards = 0,
  tvChannels,
  periodName,
  isOutright,
  title,
  flagId,
  isMultimatch,
  className,
  categoryId,
  topName,
  categoryName,
  tournamentName,
  roundName,
  status,
  sportWarning,
  categoryWarning,
  tournamentWarning,
  matchWarning,
  onElementChange,
  fromBetBuilder = false,
  simplified = false,
  allowStickiness = false,
  disableScroll = false,
  subtitleRedirection,
  myMatchViewModeIsAvailable,
}: PropsType) => {
  const scrollElement = Target.__MOBILE__ ? getAppDOMElement() : window;
  const { navbarHeight } = useViewportContext();
  const { setHeaderSize, setIsHeaderSticky, isWidgetSticky, setInitialHeaderSize } = useMatchViewContext();
  const hasStickyHeader = useMemo(() => !isWidgetSticky, [isWidgetSticky]);
  const wrapperRef = useRef(null);
  const fixedWrapperRef = useRef(null);
  const headerPosition = useRef(0);

  const [fixedHeaderSize, setFixedHeaderSize] = useState(0);
  const [fixedDesktopWidth, setFixedDesktopWidth] = useState(0);
  const [isFixed, setIsFixed] = useState(false);

  const isLive = useMemo(() => MatchEntityStatus.isLive(status), [status]);
  const isEnded = useMemo(() => MatchEntityStatus.isEnded(status), [status]);
  const showSubTitle = useMemo(() => isOutright || (!isLive && !isEnded), [isOutright, isLive, isEnded]);
  const currentTitle = useMemo(() => (isOutright && title ? title : undefined), [isOutright, title]);

  const subTitle = useMemo(() => {
    if (isMultimatch || isOutright || simplified) {
      return '';
    }
    let res = topName || categoryName || '';

    if (tournamentName && !topName) {
      const append = categoryName ? ` - ${tournamentName}` : tournamentName;
      res += append;
    }
    if (roundName) {
      const append = categoryName || tournamentName ? ` - ${roundName}` : roundName;
      res += append;
    }
    return res;
  }, [isMultimatch, topName, categoryName, tournamentName, roundName, isOutright, simplified]);

  const renderScoreboardWidget = useCallback(
    (isCompact?: boolean) => {
      if ((isLive || isEnded) && (!isOutright || isMultimatch)) {
        // NOTICE: Display live box for match that is live and not outright
        return (
          <ScoreboardWidget
            className={MatchScoreboardWidgetClassName}
            matchId={matchId}
            isCompact={isCompact}
            fromBetBuilder={fromBetBuilder}
            $isMultimatch={isMultimatch}
          />
        );
      }
      return null;
    },
    [isLive, isEnded, isOutright, matchId, fromBetBuilder, isMultimatch],
  );

  const SubTitle = useMemo(() => {
    if ((isLive || isEnded) && !isOutright) {
      return <StyledSubTitle redirection={subtitleRedirection} subTitle={subTitle} flagId={flagId} />;
    }
    return null;
  }, [flagId, isEnded, isLive, isOutright, subTitle, subtitleRedirection]);

  const Warnings = useMemo(() => {
    if (matchWarning || tournamentWarning || categoryWarning || sportWarning) {
      return (
        <WarningsWrapper>
          {[sportWarning, categoryWarning, tournamentWarning, matchWarning].map((warning) =>
            warning ? (
              <StyledInfosLabel key={warning} label={warning} type={InfosLabelTypes.INFOS_YELLOW} symbol="!" />
            ) : null,
          )}
        </WarningsWrapper>
      );
    }
    return null;
  }, [categoryWarning, matchWarning, sportWarning, tournamentWarning]);

  useLayoutEffect(() => {
    if (onElementChange && wrapperRef.current) {
      onElementChange(wrapperRef.current);
    }
  }, [renderScoreboardWidget, SubTitle, Warnings, onElementChange]);

  const computeHeaderPosition = useCallback(
    (node) => {
      if (node) {
        const { bottom, width, height } = node.getBoundingClientRect();
        const scrollY = scrollElement.scrollTop ?? scrollElement.scrollY;

        headerPosition.current = bottom + scrollY;
        wrapperRef.current = node;

        if (setInitialHeaderSize) {
          setInitialHeaderSize(height);
        }
        setFixedDesktopWidth(width);
      }
    },
    [scrollElement, setInitialHeaderSize],
  );

  const measuredFixedHeight = useCallback((node) => {
    fixedWrapperRef.current = node;

    if (node) {
      const { height } = node.getBoundingClientRect();
      setFixedHeaderSize(height);
    }
  }, []);

  const onScroll = useCallback(
    (e) => {
      if (!disableScroll) {
        const elementScrollTop = e.target.scrollTop ?? e.target.documentElement.scrollTop ?? 0;

        let scrollPositionFromNavbar = elementScrollTop;

        if (Target.__MOBILE__) {
          scrollPositionFromNavbar += navbarHeight;
        }

        setIsFixed(scrollPositionFromNavbar > headerPosition.current - FIXED_OFFSET);
      }
    },
    [navbarHeight, disableScroll],
  );

  useEffect(() => {
    scrollElement?.addEventListener('scroll', onScroll);
    return () => {
      scrollElement?.removeEventListener('scroll', onScroll);
    };
  }, [scrollElement, onScroll]);

  useEffect(() => {
    if (setHeaderSize && allowStickiness) {
      setHeaderSize(fixedHeaderSize);
    }
  }, [setHeaderSize, fixedHeaderSize, allowStickiness]);

  useEffect(() => {
    if (setIsHeaderSticky && allowStickiness) {
      setIsHeaderSticky(isFixed);
    }
  }, [setIsHeaderSticky, isFixed, allowStickiness]);

  const { styleSheet } = useCSSVariablesContext();

  const renderFixedElement = useCallback(
    () => (
      <FixedWrapper
        ref={measuredFixedHeight}
        $offsetTop={navbarHeight}
        $width={fixedDesktopWidth}
        $height={fixedHeaderSize}
        $isFixed={hasStickyHeader && isFixed}
        $CSSVariablesStyleSheet={styleSheet}
        className={className}
      >
        <StyledMyMatchDynamicBackground isActive={myMatchViewModeIsAvailable} />
        <HeaderWrapper
          ref={() => measuredFixedHeight(fixedWrapperRef.current)}
          $myMatchViewModeIsAvailable={myMatchViewModeIsAvailable}
        >
          <Header
            className={MatchHeaderClassName}
            sportId={sportId}
            startDate={matchStart}
            tvChannel={tvChannels}
            isLive={isLive}
            periodName={periodName}
            matchId={matchId}
            isOutright={isOutright}
            categoryId={categoryId}
            $myMatchViewModeIsAvailable={myMatchViewModeIsAvailable}
          />
          {showSubTitle ? (
            <StyledFullMatchTitle
              className={FullMatchTitleClassName}
              competitor1Name={competitor1Name}
              competitor2Name={competitor2Name}
              competitor1NbRedCards={competitor1NbRedCards}
              competitor2NbRedCards={competitor2NbRedCards}
              flag1={competitor1Flag}
              flag2={competitor2Flag}
              title={currentTitle}
              subtitleRedirection={subtitleRedirection}
            />
          ) : null}
        </HeaderWrapper>
        <StyledScoreboardMultimatchContainer matchId={matchId} $compact />
        {renderScoreboardWidget(true)}
        <MatchCardAnimationContainer matchId={matchId} />
        <FixedPadding />
      </FixedWrapper>
    ),
    [
      measuredFixedHeight,
      navbarHeight,
      fixedDesktopWidth,
      fixedHeaderSize,
      hasStickyHeader,
      isFixed,
      styleSheet,
      className,
      myMatchViewModeIsAvailable,
      sportId,
      matchStart,
      tvChannels,
      isLive,
      periodName,
      matchId,
      isOutright,
      categoryId,
      showSubTitle,
      competitor1Name,
      competitor2Name,
      competitor1NbRedCards,
      competitor2NbRedCards,
      competitor1Flag,
      competitor2Flag,
      currentTitle,
      subtitleRedirection,
      renderScoreboardWidget,
    ],
  );

  return (
    <>
      {Target.__MOBILE__ && scrollElement && !simplified
        ? createPortal(renderFixedElement(), scrollElement)
        : renderFixedElement()}
      <Wrapper ref={computeHeaderPosition} className={className} $CSSVariablesStyleSheet={styleSheet}>
        <StyledMyMatchDynamicBackground isActive={myMatchViewModeIsAvailable} />
        <ContentWrapper>
          <HeaderWrapper $myMatchViewModeIsAvailable={myMatchViewModeIsAvailable}>
            <Header
              className={MatchHeaderClassName}
              sportId={sportId}
              startDate={matchStart}
              tvChannel={tvChannels}
              isLive={isLive}
              periodName={periodName}
              matchId={matchId}
              isOutright={isOutright}
              categoryId={categoryId}
              $myMatchViewModeIsAvailable={myMatchViewModeIsAvailable}
            />
            {showSubTitle ? (
              <StyledFullMatchTitle
                className={FullMatchTitleClassName}
                competitor1Name={competitor1Name}
                competitor2Name={competitor2Name}
                competitor1NbRedCards={competitor1NbRedCards}
                competitor2NbRedCards={competitor2NbRedCards}
                flag1={competitor1Flag}
                flag2={competitor2Flag}
                title={currentTitle}
                subTitle={subTitle}
                flagId={flagId}
                subtitleRedirection={subtitleRedirection}
              />
            ) : null}
            <StyledScoreboardMultimatchContainer matchId={matchId} />
          </HeaderWrapper>
          {renderScoreboardWidget(simplified)}
          {!simplified ? (
            <SubHeaderWrapper $myMatchViewModeIsAvailable={myMatchViewModeIsAvailable}>
              {SubTitle}
              {Warnings}
            </SubHeaderWrapper>
          ) : null}
          <MatchCardAnimationContainer matchId={matchId} />
        </ContentWrapper>
      </Wrapper>
    </>
  );
};

export default React.memo<PropsType>(MatchHeader);
